import React, { useState } from 'react';
import { Input, Button, Space, Card, Table, Empty, Statistic, Row, Col } from 'antd';
import { SearchOutlined, ClearOutlined, PrinterOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import PermissionGuard from '../../components/PermissionGuard';
import { Modules } from '../../constants/permissions';
import { usePW930BQuery } from './hooks/usePW930BQuery';
import type { DeptStats } from '../../services/pw930bApi';
import './PW930BForm.css';

const PW930BForm: React.FC = () => {
  const { loading, data, totals, queried, noData, runQuery, reset } = usePW930BQuery();
  const [deptFrom, setDeptFrom] = useState('');
  const [deptTo, setDeptTo] = useState('');

  const handleQuery = () => {
    runQuery({ deptFrom: deptFrom.trim() || undefined, deptTo: deptTo.trim() || undefined });
  };
  const handleReset = () => { setDeptFrom(''); setDeptTo(''); reset(); };

  const handlePrint = () => {
    if (!data || data.length === 0) return;
    const win = window.open('', '_blank');
    if (!win) return;
    win.document.write(`<!DOCTYPE html><html><head><title>部門人事統計表</title>
<style>body{font-family:'Microsoft JhengHei',sans-serif;margin:20px;font-size:12px}h2{text-align:center}table{width:100%;border-collapse:collapse}th,td{border:1px solid #333;padding:3px 6px}th{background:#f0f0f0;text-align:center}.right{text-align:right}@media print{body{margin:10mm}}</style></head><body>
<h2>部門人事統計表</h2>
<table><thead><tr><th>部門代號</th><th>部門名稱</th><th>男</th><th>女</th><th>合計</th><th>已婚男</th><th>未婚男</th><th>已婚女</th><th>未婚女</th></tr></thead><tbody>
${data.map(r=>`<tr><td>${r.dept_code}</td><td>${r.dept_name}</td><td class="right">${r.male_count}</td><td class="right">${r.female_count}</td><td class="right">${r.total_count}</td><td class="right">${r.married_male}</td><td class="right">${r.unmarried_male}</td><td class="right">${r.married_female}</td><td class="right">${r.unmarried_female}</td></tr>`).join('')}
${totals?`<tr style="font-weight:bold"><td colspan="2" style="text-align:center">合計</td><td class="right">${totals.male_count}</td><td class="right">${totals.female_count}</td><td class="right">${totals.total_count}</td><td class="right">${totals.married_male}</td><td class="right">${totals.unmarried_male}</td><td class="right">${totals.married_female}</td><td class="right">${totals.unmarried_female}</td></tr>`:''}
</tbody></table></body></html>`);
    win.document.close(); win.focus(); win.print();
  };

  const columns: ColumnsType<DeptStats> = [
    { title: '部門代號', dataIndex: 'dept_code', key: 'dept_code', width: 80 },
    { title: '部門名稱', dataIndex: 'dept_name', key: 'dept_name', width: 140 },
    { title: '男', dataIndex: 'male_count', key: 'male_count', width: 60, align: 'right' as const },
    { title: '女', dataIndex: 'female_count', key: 'female_count', width: 60, align: 'right' as const },
    { title: '合計', dataIndex: 'total_count', key: 'total_count', width: 60, align: 'right' as const },
    { title: '已婚男', dataIndex: 'married_male', key: 'married_male', width: 70, align: 'right' as const },
    { title: '未婚男', dataIndex: 'unmarried_male', key: 'unmarried_male', width: 70, align: 'right' as const },
    { title: '已婚女', dataIndex: 'married_female', key: 'married_female', width: 70, align: 'right' as const },
    { title: '未婚女', dataIndex: 'unmarried_female', key: 'unmarried_female', width: 70, align: 'right' as const },
    { title: '男均齡', dataIndex: 'avg_age_male', key: 'avg_age_male', width: 70, align: 'right' as const, render: (v: number) => v?.toFixed(1) },
    { title: '女均齡', dataIndex: 'avg_age_female', key: 'avg_age_female', width: 70, align: 'right' as const, render: (v: number) => v?.toFixed(1) },
  ];

  return (
    <PermissionGuard module={Modules.PW930B} action="query">
      <div className="pw930b-container">
        <div className="pw930b-header">
          <h2>部門人事統計表</h2>
          {queried && !noData && <Button icon={<PrinterOutlined />} onClick={handlePrint}>列印</Button>}
        </div>
        <Card size="small" style={{ marginBottom: 16 }}>
          <Space wrap size="middle">
            <div>
              <label style={{ display: 'block', fontSize: 12, color: '#666', marginBottom: 4 }}>部門代號起</label>
              <Input value={deptFrom} onChange={e => setDeptFrom(e.target.value)} placeholder="起始部門" style={{ width: 130 }} allowClear />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 12, color: '#666', marginBottom: 4 }}>部門代號迄</label>
              <Input value={deptTo} onChange={e => setDeptTo(e.target.value)} placeholder="結束部門" style={{ width: 130 }} allowClear />
            </div>
            <div style={{ display: 'flex', alignItems: 'flex-end', paddingTop: 18 }}>
              <Space>
                <Button type="primary" icon={<SearchOutlined />} onClick={handleQuery} loading={loading}>查詢</Button>
                <Button icon={<ClearOutlined />} onClick={handleReset}>清除</Button>
              </Space>
            </div>
          </Space>
        </Card>
        {queried && noData && <Empty description="查無符合條件的部門資料" style={{ marginTop: 40 }} />}
        {queried && !noData && totals && (
          <Card size="small" style={{ marginBottom: 16 }}>
            <Row gutter={24}>
              <Col span={4}><Statistic title="總人數" value={totals.total_count} /></Col>
              <Col span={4}><Statistic title="男性" value={totals.male_count} /></Col>
              <Col span={4}><Statistic title="女性" value={totals.female_count} /></Col>
              <Col span={4}><Statistic title="平均年齡" value={totals.avg_age?.toFixed(1)} /></Col>
              <Col span={4}><Statistic title="平均年資" value={totals.avg_seniority?.toFixed(1)} /></Col>
            </Row>
          </Card>
        )}
        {queried && !noData && (
          <Table<DeptStats> columns={columns} dataSource={data} rowKey="dept_code" loading={loading} size="small"
            pagination={{ pageSize: 50, showSizeChanger: true, showTotal: t => `共 ${t} 筆` }} scroll={{ x: 900 }} />
        )}
      </div>
    </PermissionGuard>
  );
};

export default PW930BForm;
